import React, { memo, useMemo } from "react";
import { Box, Grid, Paper, Typography, Skeleton } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import VerifiedIcon from "@mui/icons-material/Verified";
import BoltIcon from "@mui/icons-material/Bolt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const InteractiveMiniCard = memo(
  ({ label, value, icon: Icon, accent, loading }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "12px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 2,
        border: "1px solid #eef2f6",
        transition: "all 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          borderColor: accent,
        },
      }}
    >
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={40} />
      ) : (
        <>
          <Box
            sx={{
              p: 1,
              borderRadius: "8px",
              bgcolor: `${accent}15`,
              color: accent,
              display: "flex",
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "#1e293b", fontWeight: 800, lineHeight: 1 }}
            >
              {value}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  )
);

const LeadStatsWidget = memo(({ leadData, loading }) => {
  const stats = useMemo(() => {
    const getCount = (val) => (Array.isArray(val) ? val.length : (typeof val === 'number' ? val : 0));
    return {
      total: getCount(leadData.allLeads),
      reached: getCount(leadData.reachedLeads),
      qualified: getCount(leadData.qualifiedLeads),
      onTime: getCount(leadData.slaOnTime),
      late: getCount(leadData.slaLate),
    };
  }, [leadData]);

  const cards = [
    {
      label: "New Leads",
      value: stats.total,
      icon: PeopleAltIcon,
      accent: "#6366f1",
    },
    {
      label: "Reached",
      value: stats.reached,
      icon: PhoneCallbackIcon,
      accent: "#2563eb",
    },
    {
      label: "Qualified",
      value: stats.qualified,
      icon: VerifiedIcon,
      accent: "#16a34a",
    },
    {
      label: "SLA On Time",
      value: stats.onTime,
      icon: BoltIcon,
      accent: "#eab308",
    },
    {
      label: "SLA Late",
      value: stats.late,
      icon: ErrorOutlineIcon,
      accent: "#ef4444",
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {cards.map((card, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <InteractiveMiniCard {...card} loading={loading} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

export default LeadStatsWidget;
